import { zodResolver } from '@hookform/resolvers/zod';
import { naturalName } from '@uit/shared/name';
import {
  type OnboardingContractorDraftPatch,
  type OnboardingPublicResponse,
  OnboardingPublicResponse as OnboardingPublicResponseSchema,
} from '@uit/shared/schemas/onboarding';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { LegalFooter } from '../components/LegalFooter.js';
import { DocumentUploadField } from '../components/onboarding/DocumentUploadField.js';
import { useApi } from '../lib/api.js';
import s from './ContractorOnboardingPage.module.scss';

// Form-local schema: HTML inputs are strings. Free-text fields require content;
// format-checked fields validate their pattern. Optional emails allow ''.
const emailLike = z.string().email();
const phoneRe = /^\d{3}-\d{3}-\d{4}$/;
const FormSchema = z.object({
  business_name: z.string().min(1, 'Required').max(200),
  business_address_street: z.string().min(1, 'Required').max(200),
  business_address_city: z.string().min(1, 'Required').max(120),
  business_address_state: z.string().min(1, 'Required').max(60),
  business_address_zip: z.string().min(1, 'Required').max(20),
  state_of_incorporation: z.string().min(1, 'Required').max(60),
  resource_mailing_address_street: z.string().min(1, 'Required').max(200),
  resource_mailing_address_city: z.string().min(1, 'Required').max(120),
  resource_mailing_address_state: z.string().min(1, 'Required').max(60),
  resource_mailing_address_zip: z.string().min(1, 'Required').max(20),
  mobile_phone: z.string().regex(phoneRe, 'Format: xxx-xxx-xxxx'),
  office_phone: z.string().refine((v) => v === '' || phoneRe.test(v), 'Format: xxx-xxx-xxxx'),
  business_email: emailLike,
  resource_email: emailLike,
  ach_routing: z.string().regex(/^\d{9}$/, 'Must be 9 digits'),
  ach_account: z.string().regex(/^\d{5,17}$/, 'Must be 5–17 digits'),
  ein: z.string().regex(/^\d{2}-\d{7}$/, 'Format: xx-xxxxxxx'),
  company_type: z.enum(['LLC', 'C_CORP', 'S_CORP']),
  // The resource's own mobile (T-062) — optional.
  resource_mobile_phone: z
    .string()
    .refine((v) => v === '' || phoneRe.test(v), 'Format: xxx-xxx-xxxx'),
  // Contract signer (T-130) — pre-filled, editable; required so the contract
  // always has a deliberate destination.
  contract_signer_name: z.string().min(1, 'Required').max(200),
  contract_signer_email: emailLike,
});
type FormValues = z.infer<typeof FormSchema>;

// Resource-only mode (adding a resource to an existing vendor): only the
// resource section is required; business/banking/EIN are hidden + not validated.
const ResourceOnlyFormSchema = FormSchema.pick({
  resource_email: true,
  resource_mailing_address_street: true,
  resource_mailing_address_city: true,
  resource_mailing_address_state: true,
  resource_mailing_address_zip: true,
  resource_mobile_phone: true,
  contract_signer_name: true,
  contract_signer_email: true,
});

const EMPTY: FormValues = {
  business_name: '',
  business_address_street: '',
  business_address_city: '',
  business_address_state: '',
  business_address_zip: '',
  state_of_incorporation: '',
  resource_mailing_address_street: '',
  resource_mailing_address_city: '',
  resource_mailing_address_state: '',
  resource_mailing_address_zip: '',
  mobile_phone: '',
  office_phone: '',
  business_email: '',
  resource_email: '',
  ach_routing: '',
  ach_account: '',
  ein: '',
  company_type: 'LLC',
  resource_mobile_phone: '',
  contract_signer_name: '',
  contract_signer_email: '',
};

// The server keeps one file per kind (T-212); this picks the newest anyway so a
// legacy slot with several rows never shows a stale file.
function latestDocument(
  documents: OnboardingPublicResponse['documents'],
  kind: string,
): OnboardingPublicResponse['documents'][number] | undefined {
  return documents.filter((d) => d.kind === kind).at(-1);
}

function draftToForm(
  draft: OnboardingContractorDraftPatch,
  fallbackBusinessEmail: string,
  fallbackSignerName: string,
): FormValues {
  return {
    ...EMPTY,
    // Pre-fill the business email from the admin-entered value, and the
    // contract signer (T-130) from the resource's natural name + that same
    // email; saved draft values (the contractor's overrides) win via the spread.
    business_email: fallbackBusinessEmail,
    contract_signer_name: fallbackSignerName,
    contract_signer_email: fallbackBusinessEmail,
    ...Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== null && v !== undefined)),
    company_type: draft.company_type ?? 'LLC',
  };
}

// Build a draft patch from current form values: include only fields that pass
// their per-field format (so mid-typed regex fields don't 400 the autosave).
function buildDraftPatch(values: FormValues): OnboardingContractorDraftPatch {
  const patch: Record<string, unknown> = {};
  const tryField = (key: keyof FormValues, schema: z.ZodTypeAny): void => {
    const v = values[key];
    if (v === '' || v === undefined) return;
    if (schema.safeParse(v).success) patch[key] = v;
  };
  const nonEmpty = z.string().min(1);
  tryField('business_name', nonEmpty);
  tryField('business_address_street', nonEmpty);
  tryField('business_address_city', nonEmpty);
  tryField('business_address_state', nonEmpty);
  tryField('business_address_zip', nonEmpty);
  tryField('state_of_incorporation', nonEmpty);
  tryField('resource_mailing_address_street', nonEmpty);
  tryField('resource_mailing_address_city', nonEmpty);
  tryField('resource_mailing_address_state', nonEmpty);
  tryField('resource_mailing_address_zip', nonEmpty);
  tryField('mobile_phone', z.string().regex(phoneRe));
  tryField('office_phone', z.string().regex(phoneRe));
  tryField('resource_mobile_phone', z.string().regex(phoneRe));
  tryField('business_email', emailLike);
  tryField('resource_email', emailLike);
  tryField('contract_signer_name', nonEmpty);
  tryField('contract_signer_email', emailLike);
  tryField('ach_routing', z.string().regex(/^\d{9}$/));
  tryField('ach_account', z.string().regex(/^\d{5,17}$/));
  tryField('ein', z.string().regex(/^\d{2}-\d{7}$/));
  patch['company_type'] = values.company_type;
  return patch as OnboardingContractorDraftPatch;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function ContractorOnboardingPage() {
  const { token } = useParams<{ token: string }>();
  const { data, error, loading, refetch } = useApi<OnboardingPublicResponse>(
    token ? `/api/onboard/${token}` : null,
    OnboardingPublicResponseSchema,
  );

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seededRef = useRef(false);
  // Once the contractor edits state_of_incorporation, stop mirroring the
  // business-address state into it.
  const stateOfIncTouchedRef = useRef(false);
  // Once the contractor edits the contract signer email, stop mirroring the
  // business email into it (T-130).
  const signerEmailTouchedRef = useRef(false);
  // Resource-only onboarding (T-062): validate only the resource section. A ref
  // (read at validation time) keeps the resolver identity stable across renders.
  const resourceOnlyRef = useRef(false);

  // Stable resolver that branches on the mode ref — full vs resource-only.
  const resolver = useMemo<Resolver<FormValues>>(() => {
    const full = zodResolver(FormSchema);
    // The resource-only schema validates a subset of FormValues; the cast is
    // safe because we only read the resource fields in resource-only mode.
    const resourceOnly = zodResolver(ResourceOnlyFormSchema) as unknown as Resolver<FormValues>;
    return (values, context, options) =>
      (resourceOnlyRef.current ? resourceOnly : full)(values, context, options);
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver,
    mode: 'onChange',
    defaultValues: EMPTY,
  });

  const editable = data?.editable ?? false;
  const resourceOnly = data?.resource_only ?? false;

  // Seed the form from the saved draft once, when the snapshot arrives.
  useEffect(() => {
    if (data && !seededRef.current) {
      resourceOnlyRef.current = data.resource_only;
      reset(
        draftToForm(data.draft, data.business_email, naturalName(data.contractor_resource_name)),
      );
      // A saved state_of_incorporation counts as already-edited: don't mirror over it.
      if (data.draft.state_of_incorporation) stateOfIncTouchedRef.current = true;
      // Same for a saved signer email: stop mirroring business_email into it.
      if (data.draft.contract_signer_email) signerEmailTouchedRef.current = true;
      seededRef.current = true;
      // Re-run validation under the now-correct (mode-aware) resolver.
      void trigger();
    }
  }, [data, reset, trigger]);

  // Contract signer pre-fill tracks the (editable) business email in the
  // new-vendor flow until the contractor edits the signer email themselves
  // (T-130). `type === 'change'` distinguishes user edits from setValue.
  useEffect(() => {
    if (!editable || resourceOnly) return;
    const sub = watch((value, { name, type }) => {
      if (type !== 'change') return;
      if (name === 'contract_signer_email') {
        signerEmailTouchedRef.current = true;
      } else if (name === 'business_email' && !signerEmailTouchedRef.current) {
        setValue('contract_signer_email', value.business_email ?? '', { shouldValidate: true });
      }
    });
    return () => sub.unsubscribe();
  }, [editable, resourceOnly, watch, setValue]);

  // Debounced autosave: PATCH the valid subset of fields ~800ms after a change.
  useEffect(() => {
    if (!editable || !token) return;
    const sub = watch(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void (async () => {
          setSaveState('saving');
          try {
            const res = await fetch(`/api/onboard/${token}`, {
              method: 'PATCH',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify(buildDraftPatch(getValues())),
            });
            setSaveState(res.ok ? 'saved' : 'error');
          } catch {
            setSaveState('error');
          }
        })();
      }, 800);
    });
    return () => {
      sub.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [editable, token, watch, getValues]);

  function copyBusinessToPersonal(): void {
    const v = getValues();
    setValue('resource_mailing_address_street', v.business_address_street, {
      shouldValidate: true,
    });
    setValue('resource_mailing_address_city', v.business_address_city, { shouldValidate: true });
    setValue('resource_mailing_address_state', v.business_address_state, { shouldValidate: true });
    setValue('resource_mailing_address_zip', v.business_address_zip, { shouldValidate: true });
  }

  const docKinds = new Set((data?.documents ?? []).map((d) => d.kind));
  const hasRequiredDocs = docKinds.has('w9') && docKinds.has('coi');

  const onSubmit = async (values: FormValues): Promise<void> => {
    if (!token) return;
    setSubmitState('submitting');
    setSubmitError(null);
    try {
      // The resource section is sent in both modes; resource_mobile_phone is
      // optional, so omit it when blank.
      const resourcePart = {
        resource_email: values.resource_email,
        resource_mailing_address_street: values.resource_mailing_address_street,
        resource_mailing_address_city: values.resource_mailing_address_city,
        resource_mailing_address_state: values.resource_mailing_address_state,
        resource_mailing_address_zip: values.resource_mailing_address_zip,
        ...(values.resource_mobile_phone.trim() === ''
          ? {}
          : { resource_mobile_phone: values.resource_mobile_phone }),
        // Contract signer (T-130) — sent in both modes; the API falls back to
        // the resource name / business email if these are somehow blank.
        ...(values.contract_signer_name.trim() === ''
          ? {}
          : { contract_signer_name: values.contract_signer_name }),
        ...(values.contract_signer_email.trim() === ''
          ? {}
          : { contract_signer_email: values.contract_signer_email }),
      };
      // Resource-only: just the resource section (the vendor already exists).
      // New-vendor flow: the full business + banking + resource payload.
      const payload: Record<string, unknown> = resourceOnly
        ? resourcePart
        : {
            business_name: values.business_name,
            business_address_street: values.business_address_street,
            business_address_city: values.business_address_city,
            business_address_state: values.business_address_state,
            business_address_zip: values.business_address_zip,
            state_of_incorporation: values.state_of_incorporation,
            mobile_phone: values.mobile_phone,
            ...(values.office_phone.trim() === '' ? {} : { office_phone: values.office_phone }),
            business_email: values.business_email,
            ach_routing: values.ach_routing,
            ach_account: values.ach_account,
            ein: values.ein,
            company_type: values.company_type,
            ...resourcePart,
          };
      const res = await fetch(`/api/onboard/${token}/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        if (res.status === 400) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setSubmitError(
            body.error === 'missing_required_documents'
              ? 'Please upload your W-9 and Certificate of Insurance before submitting.'
              : 'Please check the form for errors and try again.',
          );
        } else if (res.status === 409 || res.status === 410) {
          setSubmitError('This onboarding link is no longer active.');
        } else if (res.status === 502) {
          // BoldSign send failure (T-145): an upstream e-sign fault, not the
          // contractor's input — tell them it's retryable, not an opaque 500.
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setSubmitError(
            body.error === 'esign_send_failed'
              ? 'We could not send your contract for signature. Please try again, or contact ultimumIT if it keeps happening.'
              : `Could not submit (${res.status}).`,
          );
        } else {
          setSubmitError(`Could not submit (${res.status}).`);
        }
        setSubmitState('error');
        return;
      }
      setSubmitState('done');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      setSubmitState('error');
    }
  };

  // ── Render branches ────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <Shell>
        <div className={s.loading}>Loading…</div>
      </Shell>
    );
  }

  if (error) {
    const expired = error.message.includes('410');
    return (
      <Shell>
        <div className={s.closed}>
          <h1 className={s.closedTitle}>{expired ? 'This link has expired' : 'Link not found'}</h1>
          <p className={s.closedBody}>
            {expired
              ? 'Your onboarding link has expired or was cancelled. Please contact your ultimumIT representative for a new one.'
              : 'We could not find this onboarding link. Double-check the URL from your email.'}
          </p>
        </div>
      </Shell>
    );
  }

  if (!data) return null;

  if (submitState === 'done') {
    return (
      <Shell>
        <div className={s.success}>
          <h1 className={s.successTitle}>Thank you — your information is in.</h1>
          <p className={s.successBody}>
            Next, you’ll receive the contract to review and sign. Keep an eye on{' '}
            <strong>{data.business_email}</strong>.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className={s.head}>
        <h1 className={s.title}>Welcome — complete your onboarding with ultimumIT</h1>
        <p className={s.subtitle}>
          Please complete the business and resource details below for {data.client.name}. Your
          contract will follow by email.
        </p>
      </header>

      <section className={s.summaryCard}>
        <h2 className={s.sectionTitle}>Your engagement</h2>
        <dl className={s.summaryGrid}>
          <div>
            <dt>Client</dt>
            <dd>{data.client.name}</dd>
          </div>
          <div>
            <dt>Project / product</dt>
            <dd>{data.client.project_or_product_name}</dd>
          </div>
          <div>
            <dt>Rate to you</dt>
            <dd>${data.rate_to_contractor.toFixed(2)}</dd>
          </div>
          <div>
            <dt>Start</dt>
            <dd>{data.start_date}</dd>
          </div>
          <div>
            <dt>End</dt>
            <dd>{data.end_date}</dd>
          </div>
        </dl>
      </section>

      {!editable && (
        <div className={s.readonlyBanner} role="status">
          You’ve already submitted this onboarding. It’s now read-only.
        </div>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <fieldset className={s.fieldset} disabled={!editable}>
          {resourceOnly && (
            <div className={s.readonlyBanner} role="status">
              You&apos;re joining an existing vendor
              {data.vendor_business_name ? ` — ${data.vendor_business_name}` : ''}. Just your
              resource details are needed; the business and banking info are already on file.
            </div>
          )}

          {/* ── BUSINESS INFORMATION (first) — new-vendor flow only ──────── */}
          {!resourceOnly && (
            <>
              <section className={s.formSection}>
                <h2 className={s.sectionTitle}>Business information</h2>
            <p className={s.sectionNote}>
              About the company ultimumIT contracts with (the vendor).
            </p>
            <Field id="business_name" label="Business name (vendor)" error={errors.business_name}>
              <input id="business_name" className={s.input} {...register('business_name')} />
            </Field>
            <Field id="business_email" label="Business email" error={errors.business_email}>
              <input
                id="business_email"
                type="email"
                className={s.input}
                {...register('business_email')}
              />
              <p className={s.fieldHint}>
                The company&apos;s contact inbox. It also pre-fills the contract signer below until
                you change the signer directly.
              </p>
            </Field>
            <div className={s.row}>
              <Field
                id="mobile_phone"
                label="Mobile phone"
                hint="xxx-xxx-xxxx"
                error={errors.mobile_phone}
              >
                <input id="mobile_phone" className={s.input} {...register('mobile_phone')} />
              </Field>
              <Field id="office_phone" label="Office phone (optional)" error={errors.office_phone}>
                <input id="office_phone" className={s.input} {...register('office_phone')} />
              </Field>
            </div>
            <Field id="company_type" label="Company type" error={errors.company_type}>
              <select id="company_type" className={s.input} {...register('company_type')}>
                <option value="LLC">LLC</option>
                <option value="C_CORP">C-Corp</option>
                <option value="S_CORP">S-Corp</option>
              </select>
            </Field>
          </section>

          <section className={s.formSection}>
            <h2 className={s.sectionTitle}>Business address</h2>
            <Field
              id="business_address_street"
              label="Street"
              error={errors.business_address_street}
            >
              <input
                id="business_address_street"
                className={s.input}
                {...register('business_address_street')}
              />
            </Field>
            <div className={s.row}>
              <Field id="business_address_city" label="City" error={errors.business_address_city}>
                <input
                  id="business_address_city"
                  className={s.input}
                  {...register('business_address_city')}
                />
              </Field>
              <Field
                id="business_address_state"
                label="State"
                error={errors.business_address_state}
              >
                <input
                  id="business_address_state"
                  className={s.input}
                  {...register('business_address_state', {
                    // Mirror the business state into state of incorporation
                    // until the contractor edits that field themselves.
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!stateOfIncTouchedRef.current) {
                        setValue('state_of_incorporation', e.target.value, {
                          shouldValidate: true,
                        });
                      }
                    },
                  })}
                />
              </Field>
              <Field id="business_address_zip" label="ZIP" error={errors.business_address_zip}>
                <input
                  id="business_address_zip"
                  className={s.input}
                  {...register('business_address_zip')}
                />
              </Field>
            </div>
            <Field
              id="state_of_incorporation"
              label="State of incorporation"
              error={errors.state_of_incorporation}
            >
              <input
                id="state_of_incorporation"
                className={s.input}
                {...register('state_of_incorporation', {
                  onChange: () => {
                    stateOfIncTouchedRef.current = true;
                  },
                })}
              />
            </Field>
          </section>

          <section className={s.formSection}>
            <h2 className={s.sectionTitle}>Banking (encrypted)</h2>
            <p className={s.sectionNote}>
              Your ACH and EIN are encrypted at rest and only visible to ultimumIT admins.
            </p>
            <div className={s.row}>
              <Field id="ach_routing" label="ACH routing" hint="9 digits" error={errors.ach_routing}>
                <input
                  id="ach_routing"
                  inputMode="numeric"
                  className={s.input}
                  {...register('ach_routing')}
                />
              </Field>
              <Field id="ach_account" label="ACH account" hint="5–17 digits" error={errors.ach_account}>
                <input
                  id="ach_account"
                  inputMode="numeric"
                  className={s.input}
                  {...register('ach_account')}
                />
              </Field>
            </div>
            <Field id="ein" label="EIN" hint="xx-xxxxxxx" error={errors.ein}>
              <input id="ein" className={s.input} {...register('ein')} />
            </Field>
          </section>
            </>
          )}

          {/* ── CONTRACT SIGNER (T-130) — both modes ────────────────────── */}
          <section className={s.formSection}>
            <h2 className={s.sectionTitle}>Contract signer</h2>
            <p className={s.sectionNote}>
              The subcontractor agreement will be sent to this person to review and sign. Change
              this if someone else (e.g. the company owner) signs on the vendor&apos;s behalf.
            </p>
            <div className={s.row}>
              <Field
                id="contract_signer_name"
                label="Signer name"
                error={errors.contract_signer_name}
              >
                <input
                  id="contract_signer_name"
                  className={s.input}
                  {...register('contract_signer_name')}
                />
              </Field>
              <Field
                id="contract_signer_email"
                label="Signer email"
                error={errors.contract_signer_email}
              >
                <input
                  id="contract_signer_email"
                  type="email"
                  className={s.input}
                  {...register('contract_signer_email')}
                />
              </Field>
            </div>
          </section>

          {/* ── RESOURCE INFORMATION (last) ─────────────────────────────── */}
          <section className={s.formSection}>
            <h2 className={s.sectionTitle}>Resource information</h2>
            <p className={s.sectionNote}>About the individual doing the work.</p>
            <div className={s.row}>
              <Field id="resource_email" label="Resource email" error={errors.resource_email}>
                <input
                  id="resource_email"
                  type="email"
                  className={s.input}
                  {...register('resource_email')}
                />
              </Field>
              <Field
                id="resource_mobile_phone"
                label="Mobile phone (optional)"
                hint="xxx-xxx-xxxx"
                error={errors.resource_mobile_phone}
              >
                <input
                  id="resource_mobile_phone"
                  className={s.input}
                  {...register('resource_mobile_phone')}
                />
              </Field>
            </div>

            <div className={s.sectionHeadRow}>
              <h3 className={s.sectionTitle}>Resource mailing address</h3>
              {!resourceOnly && (
                <label className={s.copyToggle}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) copyBusinessToPersonal();
                    }}
                  />
                  Same as business
                </label>
              )}
            </div>
            <Field
              id="resource_mailing_address_street"
              label="Street"
              error={errors.resource_mailing_address_street}
            >
              <input
                id="resource_mailing_address_street"
                className={s.input}
                {...register('resource_mailing_address_street')}
              />
            </Field>
            <div className={s.row}>
              <Field
                id="resource_mailing_address_city"
                label="City"
                error={errors.resource_mailing_address_city}
              >
                <input
                  id="resource_mailing_address_city"
                  className={s.input}
                  {...register('resource_mailing_address_city')}
                />
              </Field>
              <Field
                id="resource_mailing_address_state"
                label="State"
                error={errors.resource_mailing_address_state}
              >
                <input
                  id="resource_mailing_address_state"
                  className={s.input}
                  {...register('resource_mailing_address_state')}
                />
              </Field>
              <Field
                id="resource_mailing_address_zip"
                label="ZIP"
                error={errors.resource_mailing_address_zip}
              >
                <input
                  id="resource_mailing_address_zip"
                  className={s.input}
                  {...register('resource_mailing_address_zip')}
                />
              </Field>
            </div>
          </section>
        </fieldset>

        {!resourceOnly && (
          <section className={s.formSection}>
            <h2 className={s.sectionTitle}>Business documents</h2>
            <p className={s.sectionNote}>PDF, PNG, or JPG. 10 MB max per file.</p>
          {token && (
            <div className={s.uploads}>
              <DocumentUploadField
                token={token}
                kind="w9"
                label="W-9"
                required
                existing={latestDocument(data.documents, 'w9')}
                disabled={!editable}
                onUploaded={() => void refetch()}
              />
              <DocumentUploadField
                token={token}
                kind="coi"
                label="Certificate of Insurance (COI)"
                required
                existing={latestDocument(data.documents, 'coi')}
                disabled={!editable}
                onUploaded={() => void refetch()}
              />
              <DocumentUploadField
                token={token}
                kind="voided_check"
                label="Voided check"
                required={false}
                existing={latestDocument(data.documents, 'voided_check')}
                disabled={!editable}
                onUploaded={() => void refetch()}
              />
              <DocumentUploadField
                token={token}
                kind="cert_of_incorporation"
                label="Certificate of Incorporation"
                required={false}
                existing={latestDocument(data.documents, 'cert_of_incorporation')}
                disabled={!editable}
                onUploaded={() => void refetch()}
              />
            </div>
            )}
          </section>
        )}

        <section className={s.formSection}>
          <h2 className={s.sectionTitle}>Resource documents</h2>
          <p className={s.sectionNote}>About the individual doing the work. Optional.</p>
          {token && (
            <div className={s.uploads}>
              <DocumentUploadField
                token={token}
                kind="driver_license"
                label="Driver’s license"
                required={false}
                existing={latestDocument(data.documents, 'driver_license')}
                disabled={!editable}
                onUploaded={() => void refetch()}
              />
              <DocumentUploadField
                token={token}
                kind="visa"
                label="Visa documentation"
                required={false}
                existing={latestDocument(data.documents, 'visa')}
                disabled={!editable}
                onUploaded={() => void refetch()}
              />
            </div>
          )}
        </section>

        {editable && (
          <div className={s.footer}>
            <div className={s.saveIndicator} aria-live="polite">
              {saveState === 'saving' && 'Saving…'}
              {saveState === 'saved' && 'Saved'}
              {saveState === 'error' && 'Save failed — keep going, we’ll retry'}
            </div>
            {submitError && (
              <div className={s.submitError} role="alert">
                {submitError}
              </div>
            )}
            <button
              type="submit"
              className={s.submit}
              disabled={
                !isValid || (!resourceOnly && !hasRequiredDocs) || submitState === 'submitting'
              }
            >
              {submitState === 'submitting' ? 'Submitting…' : 'Submit onboarding'}
            </button>
            {!resourceOnly && !hasRequiredDocs && (
              <div className={s.gateHint}>Upload your W-9 and COI to enable submit.</div>
            )}
          </div>
        )}
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className={s.page}>
      <div className={s.brand}>ultimumIT</div>
      <div className={s.container}>{children}</div>
      <LegalFooter />
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  /** Small helper text below the input (e.g. format hints). Keeping format
   *  details here instead of in the label keeps labels short so they don't wrap
   *  in the narrow grid columns and misalign their inputs. */
  hint?: string;
  error?: { message?: string | undefined } | undefined;
  children: React.ReactNode;
}

function Field({ id, label, hint, error, children }: FieldProps) {
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && <div className={s.fieldHint}>{hint}</div>}
      {error?.message && <div className={s.fieldError}>{error.message}</div>}
    </div>
  );
}
